/**
 * @file AdminRoute.tsx
 * @description Route wrapper to protect admin-only pages
 * @author XiaVan Team
 * 
 * Verifies user has admin role before allowing access
 * Redirects to home page if unauthorized
 */

import { Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useAdminAuth } from '@/hooks/useAdminAuth'

/**
 * Props for AdminRoute component
 */
interface AdminRouteProps {
  children: React.ReactNode
}

/**
 * Route protection for admin-only pages
 * 
 * Checks:
 * 1. User is authenticated
 * 2. User has admin role in Firestore
 * 
 * @param {AdminRouteProps} props - Children to render if authorized
 * @returns Protected route or redirect
 * 
 * @example
 * <Route path="/admin" element={
 *   <AdminRoute>
 *     <AdminPage />
 *   </AdminRoute>
 * } />
 */
export default function AdminRoute({ children }: AdminRouteProps) {
  const { user, loading: authLoading } = useAuth()
  const { isAdmin, loading: adminLoading } = useAdminAuth()

  // Show nothing while checking auth/role
  if (authLoading || adminLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-muted-foreground">Verificando permisos...</p>
        </div>
      </div>
    )
  }

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  // Render children if admin
  return <>{children}</>
}
